import { useState } from 'react';

import { Markdown } from './markdown';

import type { ContentCheck } from '@mio/shared';

export interface ActivityChecksProps {
  checks: ContentCheck[];
  /** Checkpoints keep answers locked until the learner has submitted. */
  locked?: boolean;
}

/**
 * Comprehension checks at the end of a lesson. Answers stay hidden until the learner
 * asks for them, so reading the question is a retrieval attempt, not a lookup.
 */
export function ActivityChecks({ checks, locked = false }: ActivityChecksProps) {
  const [revealed, setRevealed] = useState<Set<number>>(() => new Set());
  if (checks.length === 0) return null;

  const toggle = (index: number) =>
    setRevealed((current) => {
      const next = new Set(current);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });

  return (
    <section className="mt-12" aria-labelledby="checks-title">
      <h2 id="checks-title" className="text-fg text-[20px] font-medium tracking-[-0.02em]">
        Check yourself
      </h2>
      <p className="text-fg-muted mt-2 text-[14px] leading-6">
        {locked
          ? 'Answer each question in writing before you submit. Model answers unlock once the checkpoint is complete.'
          : 'Answer in your own words first. Reveal the answer only to compare.'}
      </p>
      <ol className="mt-6 space-y-6">
        {checks.map((check, index) => {
          const open = revealed.has(index);
          return (
            <li key={check.question} className="border-line rounded-md border p-5">
              <p className="text-fg text-[15px] leading-7">
                <span className="text-fg-muted mr-2 tabular-nums">{index + 1}.</span>
                {check.question}
              </p>
              {locked ? null : (
                <button
                  type="button"
                  onClick={() => toggle(index)}
                  aria-expanded={open}
                  className="text-fg-muted hover:text-fg mt-3 text-[13px] font-medium underline-offset-4 hover:underline"
                >
                  {open ? 'Hide answer' : 'Reveal answer'}
                </button>
              )}
              {open && !locked ? (
                <div className="border-line mt-3 border-t pt-3 text-[14.5px]">
                  <Markdown source={check.answer} />
                </div>
              ) : null}
            </li>
          );
        })}
      </ol>
    </section>
  );
}
